import { useState } from 'react';
import { Link } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { InlineNotification, Pagination, Select, SelectItem } from '@carbon/react';
import type { ColumnDef } from '@tanstack/react-table';
import { formatVnd } from '@ruby-cms/shared';
import { api } from '../api/client';
import { DataTable } from '../components/DataTable';
import { PageHeader } from '../components/states';
import { Money, When } from '../components/format';

type Mismatch = {
  partnerId: string; code: string | null; displayName: string | null;
  wallet: string; ledger: string; walletDiff: string;
  reserved: string; openPayouts: string; reservedDiff: string;
  paid: string; paidPayouts: string; paidDiff: string;
};
type Stuck = {
  id: string; rmlId: number; gameUserId: number | null; nickname: string; game: string; kind: string; state: string;
  amount: string; refundedAmount: string; serviceName: string | null; transTime: string; attempts: number; lastError: string | null;
};
type Recon = {
  unmatched: Stuck[]; total: number; page: number; pageSize: number; counts: Record<string, number>;
  partners: { checked: number; mismatched: Mismatch[] };
};

const STATE: Record<string, string> = {
  UNMATCHED: 'Hoàn cược không khớp cược',
  DEAD: 'Lỗi, đã dừng xử lý',
  WAITING_ACCRUAL: 'Cược chưa có đại lý',
};

// any difference is an error, whichever way it goes
function Diff({ v }: { v: string }) {
  const n = BigInt(v);
  return <span className={n === 0n ? 'num' : 'num num--neg'}>{n > 0n ? '+' : ''}{formatVnd(n)}</span>;
}

const partnerCols: ColumnDef<Mismatch, any>[] = [
  { header: 'Đại lý', id: 'p', cell: (c) => <Link to={'/agents/' + c.row.original.partnerId}>{c.row.original.code ?? '-'} · {c.row.original.displayName ?? ''}</Link> },
  { header: 'Ví (tổng các ngăn)', accessorKey: 'wallet', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Theo sổ hoa hồng', accessorKey: 'ledger', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Lệch ví', accessorKey: 'walletDiff', cell: (c) => <Diff v={c.getValue()} /> },
  { header: 'Lệch giữ chi', accessorKey: 'reservedDiff', cell: (c) => <Diff v={c.getValue()} /> },
  { header: 'Lệch đã chi', accessorKey: 'paidDiff', cell: (c) => <Diff v={c.getValue()} /> },
];
const stuckCols: ColumnDef<Stuck, any>[] = [
  { header: 'Lúc', accessorKey: 'transTime', cell: (c) => <When v={c.getValue()} /> },
  { header: 'Người chơi', id: 'u', cell: (c) => c.row.original.gameUserId
    ? <Link to={'/users/' + c.row.original.gameUserId}>{c.row.original.nickname}</Link> : c.row.original.nickname },
  { header: 'Game', accessorKey: 'game' },
  { header: 'Dòng sổ', accessorKey: 'rmlId', cell: (c) => <span className="num">{c.getValue()}</span> },
  { header: 'Loại', accessorKey: 'kind', cell: (c) => (c.getValue() === 'REFUND' ? 'Hoàn cược' : c.getValue() === 'BET' ? 'Cược' : c.getValue()) },
  { header: 'Số tiền', accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Đã khớp', accessorKey: 'refundedAmount', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Tình trạng', accessorKey: 'state', cell: (c) => STATE[c.getValue()] ?? c.getValue() },
  { header: 'Lỗi', accessorKey: 'lastError', cell: (c) => c.getValue() ?? '-' },
];

export function AgentReconPage() {
  const [state, setState] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const q = useQuery({
    queryKey: ['agent-recon', state, page, pageSize],
    queryFn: () => api.get<Recon>('/api/admin/agent-recon', { state, page, pageSize }),
    placeholderData: keepPreviousData,
  });
  const d = q.data;
  const bad = d?.partners.mismatched ?? [];
  return (
    <div className="page">
      <PageHeader title="Đối soát hoa hồng"
        lead="Mỗi ví hoa hồng phải bằng đúng sổ hoa hồng của đại lý đó, và tiền giữ chi, đã chi phải bằng các yêu cầu rút. Bên dưới là các dòng sổ game mà việc tính hoa hồng chưa xử lý xong." />
      {d ? (
        <div className="kpis">
          <div className="kpi"><span className="kpi__label">Ví đã kiểm</span><strong className="kpi__value num">{d.partners.checked}</strong></div>
          <div className="kpi"><span className="kpi__label">Ví lệch</span><strong className={'kpi__value num' + (bad.length ? ' num--neg' : '')}>{bad.length}</strong></div>
          {Object.keys(STATE).map((k) => (
            <div key={k} className="kpi"><span className="kpi__label">{STATE[k]}</span><strong className="kpi__value num">{d.counts[k] ?? 0}</strong></div>
          ))}
        </div>
      ) : null}
      <section className="section">
        <h2>Ví lệch sổ</h2>
        {d && bad.length === 0 ? (
          <InlineNotification kind="success" lowContrast hideCloseButton title="Khớp" subtitle={`Cả ${d.partners.checked} ví hoa hồng khớp với sổ.`} />
        ) : (
          <DataTable columns={partnerCols} data={d ? bad : undefined} loading={q.isLoading} error={q.error} onRetry={() => q.refetch()}
            rowKey={(r) => r.partnerId} empty={{ title: 'Không có ví lệch' }} />
        )}
      </section>
      <section className="section">
        <h2>Dòng sổ chưa xử lý xong</h2>
        <div className="toolbar-filters">
          <Select id="rc-st" labelText="Tình trạng" value={state} onChange={(e) => { setState(e.target.value); setPage(1); }}>
            <SelectItem value="" text="Tất cả" />
            {Object.keys(STATE).map((k) => <SelectItem key={k} value={k} text={STATE[k]!} />)}
          </Select>
        </div>
        <DataTable columns={stuckCols} data={d?.unmatched} loading={q.isLoading || q.isPlaceholderData} error={q.error}
          onRetry={() => q.refetch()} rowKey={(r) => r.id}
          empty={{ title: 'Không còn dòng nào', body: 'Mọi cược và hoàn cược đã được tính hoa hồng hoặc bỏ qua đúng luật.' }} />
        {d && d.total > 0 ? (
          <Pagination page={page} pageSize={pageSize} pageSizes={[20, 50, 100]} totalItems={d.total}
            onChange={({ page: p, pageSize: s }) => { setPage(p); setPageSize(s); }} />
        ) : null}
      </section>
    </div>
  );
}
